/**
 * CHARSTAN Platform Date Formatting Utility
 * ==========================================
 * Single source of truth for all frontend date display.
 *
 * Canonical rules (ref: /app/memory/TEMPORAL_STANDARD.md):
 * - Backend stores UTC datetime objects / ISO 8601 strings
 * - Frontend displays localized dates derived from canonical UTC
 * - Exports use UTC / ISO 8601
 * - No ad-hoc per-page date formatting
 *
 * Supported locales: 'en' (en-US), 'pt-BR'
 */

const LOCALE_MAP = {
  en: "en-US",
  "en-US": "en-US",
  "pt-BR": "pt-BR",
  pt: "pt-BR",
};

function resolveLocale(locale) {
  return LOCALE_MAP[locale] || LOCALE_MAP.en;
}

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") return new Date(value > 1e12 ? value : value * 1000);
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Format a date value for display (date only).
 * Example: "Apr 6, 2026" (en) or "06/04/2026" (pt-BR)
 */
export function formatDate(value, locale = "en") {
  const d = parseDate(value);
  if (!d) return "";
  return d.toLocaleDateString(resolveLocale(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a date value for display (date + time).
 * Example: "Apr 6, 2026, 2:30 PM" (en) or "06/04/2026, 14:30" (pt-BR)
 */
export function formatDateTime(value, locale = "en") {
  const d = parseDate(value);
  if (!d) return "";
  return d.toLocaleString(resolveLocale(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format for regulatory exports — always UTC ISO 8601.
 * Example: "2026-04-06T14:30:15.000Z"
 */
export function formatUTCForExport(value) {
  const d = parseDate(value);
  if (!d) return "";
  return d.toISOString();
}

/**
 * Format as date-only ISO string (YYYY-MM-DD) in UTC.
 */
export function formatISODate(value) {
  const d = parseDate(value);
  if (!d) return "";
  return d.toISOString().slice(0, 10);
}

/**
 * Relative time display: "2 hours ago", "3 days ago", etc.
 * Falls back to formatDate for dates older than 30 days.
 */
export function formatRelativeTime(value, locale = "en") {
  const d = parseDate(value);
  if (!d) return "";
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  const resolved = resolveLocale(locale);
  if (diffSec < 60) return resolved === "pt-BR" ? "agora mesmo" : "just now";
  if (diffMin < 60) return resolved === "pt-BR" ? `${diffMin}min` : `${diffMin}m ago`;
  if (diffHour < 24) return resolved === "pt-BR" ? `${diffHour}h` : `${diffHour}h ago`;
  if (diffDay < 30) return resolved === "pt-BR" ? `${diffDay}d` : `${diffDay}d ago`;
  return formatDate(value, locale);
}

/**
 * Check if a date string/value is valid and parseable.
 */
export function isValidDate(value) {
  return parseDate(value) !== null;
}
